import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Translate } from 'react-localize-redux';
import CategoryFeedback from './CategoryFeedback';
import { QcCategorySection } from './qcCategories';
import {
    CorrectionFlagEvent,
    CorrectionFlagView,
    isFlagOpen,
    latestFlaggingEvent,
    personName,
} from './correctionFlag';

interface CorrectionFlagBannerProps {
    flag: CorrectionFlagView;
    /** The study's VISIT reasons, to name the reason ids the flag carries. */
    reasonSections: QcCategorySection[];
    /** The active language code, for the dates — see ReviewRail's `localeCode`. */
    localeCode: string;
    /** Opens the patient record on this visit, where the correction is made. */
    onOpenPatientRecord: () => void;
}

const NO_OP = () => undefined;

/**
 * SKIN-577 — the visit's correction flag, stated where the reviewer reads the visit.
 *
 * Open (OPEN or CORRECTED): why it was sent back — the reasons and the note of the step that
 * sent it, the latest OPEN or REOPEN — who sent it and when, who is to correct it, and the way
 * to the patient record where the correction is made. Resolved: the history only. Never
 * flagged: nothing.
 *
 * The history is the flag's whole log, oldest first. It is append-only on the server, so it is
 * rendered as given; nothing here summarises or reorders it.
 */
export default function CorrectionFlagBanner({
    flag,
    reasonSections,
    localeCode,
    onOpenPatientRecord,
}: CorrectionFlagBannerProps) {
    const [showHistory, setShowHistory] = useState(false);
    const current = flag.current;
    if (!current) return null;

    const open = isFlagOpen(current.state);
    const flagging = latestFlaggingEvent(flag);
    const raisedBy = personName(flagging?.actor ?? null);
    const assignee = personName(current.assignee);
    const date = (iso: string) => new Date(iso).toLocaleString(localeCode);

    return (
        <Alert
            severity={open ? 'warning' : 'success'}
            icon={<FlagOutlinedIcon fontSize="inherit" />}
            data-testid="correction-flag-banner"
            data-state={current.state}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
        >
            <AlertTitle>
                <Translate id={`hospital.quality-check.correction.banner.title.${current.state}`} />
            </AlertTitle>

            {open && (
                <React.Fragment>
                    {/* The reasons and the note, read-only, as the image and lesion feedback show theirs. */}
                    <CategoryFeedback
                        readOnly
                        categories={{ status: 'ready', sections: reasonSections }}
                        value={{ categoryIds: current.reasonFieldIds, comment: flagging?.note ?? '' }}
                        onChange={NO_OP}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }} data-testid="flag-raised-by">
                        <Translate id="hospital.quality-check.correction.banner.raised-by" />
                        {` ${raisedBy ?? '—'}`}
                        {flagging && `, ${date(flagging.at)}`}
                    </Typography>
                    <Typography variant="caption" display="block" data-testid="flag-assignee">
                        <Translate id="hospital.quality-check.correction.banner.assignee" />
                        {` ${assignee ?? '—'}`}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        onClick={onOpenPatientRecord}
                        data-testid="open-patient-record"
                        sx={{ mt: 1.5 }}
                    >
                        <Translate id="hospital.quality-check.correction.banner.open-patient-record" />
                    </Button>
                </React.Fragment>
            )}

            <Box sx={{ mt: 1 }}>
                <Button
                    size="small"
                    color="inherit"
                    onClick={() => setShowHistory((shown) => !shown)}
                    data-testid="toggle-flag-history"
                    sx={{ px: 0, textDecoration: 'underline' }}
                >
                    <Translate
                        id={showHistory
                            ? 'hospital.quality-check.correction.banner.hide-history'
                            : 'hospital.quality-check.correction.banner.show-history'}
                    />
                    {` (${flag.history.length})`}
                </Button>
                {showHistory && (
                    <Box component="ol" sx={{ m: 0, mt: 0.5, pl: 0, listStyle: 'none' }}>
                        {flag.history.map((event, index) => (
                            <HistoryEntry key={index} event={event} when={date(event.at)} />
                        ))}
                    </Box>
                )}
            </Box>
        </Alert>
    );
}

/** One step: what was done, by whom, when, and its note when it has one. */
function HistoryEntry({ event, when }: { event: CorrectionFlagEvent; when: string }) {
    return (
        <Box component="li" data-testid="flag-history-entry" data-action={event.action} sx={{ mb: 0.5 }}>
            <Typography variant="caption" display="block">
                <Box component="span" sx={{ fontWeight: 600 }}>
                    <Translate id={`hospital.quality-check.correction.banner.action.${event.action}`} />
                </Box>
                {` · ${personName(event.actor) ?? '—'} · ${when}`}
                {event.action === 'REASSIGN' && ` → ${personName(event.assignee) ?? '—'}`}
            </Typography>
            {event.note && (
                <Typography variant="caption" color="text.secondary" display="block">
                    {event.note}
                </Typography>
            )}
        </Box>
    );
}
